import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, logger } from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes("session_id=")) {
        const sessionId = hash.split("session_id=")[1].split("&")[0];
        window.history.replaceState(null, "", window.location.pathname);
        try {
          const { data } = await api.post("/auth/session", { session_id: sessionId });
          setUser(data.user);
          setLoading(false);
          return;
        } catch (err) {
          logger.error("Google session exchange failed", err);
        }
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(null);
      }
      setLoading(false);
    };
    init();
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const signup = useCallback(async (name, email, password) => {
    const { data } = await api.post("/auth/signup", { name, email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const loginWithGoogle = useCallback(() => {
    const redirect = `${window.location.origin}/account`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      logger.error("Logout request failed", err);
    }
    setUser(null);
  }, []);

  const actions = useMemo(
    () => ({ login, signup, loginWithGoogle, logout }),
    [login, signup, loginWithGoogle, logout]
  );

  const value = useMemo(() => ({ user, loading, ...actions }), [user, loading, actions]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
